/**
 * Step5GenerateProposal
 *
 * Final wizard step. User picks a built-in proposal template, edits the
 * body inline (placeholders are filled live), previews the rendered PDF
 * in a modal, and downloads it.
 */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { jsPDF } from 'jspdf';

import { PROPOSAL_TEMPLATES, DEFAULT_PROPOSAL_VARIABLES } from '../../constants/proposalTemplates';
import { formatCurrency } from '../../utils/bidWizardHelpers';
import logoContrast from '../../../../assets/images/logos/logo_contrast.png';

// ── Brand palette ───────────────────────────────────────────────────────────
// Mirrors tailwind.config.js — kept inline so jsPDF (which doesn't know
// Tailwind) can use the exact same values as the on-screen UI.
const BRAND = {
  orange:   '#f58025',
  darkblue: '#152451',
  teal:     '#009391',
  grey:     '#54576c',
  greyLine: '#dcdeef',
};

// Use the orange-on-transparent ScaleTime mark so the logo reads on the
// white PDF page. (The white-on-transparent variant in /public is meant for
// dark backgrounds like the sidebar.)
const LOGO_PATH = logoContrast;

const todayLabel = () =>
  new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const defaultServicesText = (services) => {
  if (!services?.length) return '- Services to be defined';
  return services
    .map((svc) => {
      const label = svc.category || svc.name || 'Services';
      const hours = (svc.items || []).reduce((sum, i) => sum + (Number(i.hours) || 0), 0);
      return hours > 0 ? `- ${label} (${Math.round(hours)} hrs)` : `- ${label}`;
    })
    .join('\n');
};

const fillPlaceholders = (text, vars) =>
  Object.entries(vars).reduce(
    (acc, [key, value]) => acc.replaceAll(`{{${key}}}`, value ?? ''),
    text,
  );

// Convert /scale_logo.png → data URL + natural dimensions, so jsPDF can embed
// it without making a network request at render time.
const loadLogoData = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          width:   img.naturalWidth,
          height:  img.naturalHeight,
        });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = reject;
    img.src = src;
  });

const Step5GenerateProposal = ({ wizardData, calculations }) => {
  const isRetainer = wizardData.engagementType === 'retainer';
  // Prefer the marked-up bid value Step 4 publishes to wizardData; fall back
  // to the cost-basis figure from useBidWizardState if it's not present yet.
  // Step 4 already computes monthly-vs-total per engagement type, so we use
  // its number directly without re-dividing.
  const bidValue =
    wizardData.bidCalculations?.bidValue ??
    calculations?.bidValue ??
    0;

  const [selectedTemplateId, setSelectedTemplateId] = useState(
    isRetainer ? 'retainer-monthly' : 'formal-project',
  );
  const selectedTemplate = useMemo(
    () => PROPOSAL_TEMPLATES.find((t) => t.id === selectedTemplateId) || PROPOSAL_TEMPLATES[0],
    [selectedTemplateId],
  );

  // Editable body — reset to template's default whenever template changes
  const [bodyText, setBodyText] = useState(selectedTemplate.body);
  useEffect(() => {
    setBodyText(selectedTemplate.body);
  }, [selectedTemplate.body]);

  // Editable client name — seeded from wizard data, then user can override
  const wizardClientName =
    wizardData.clientName ||
    wizardData.selectedTemplate?.clientName ||
    '';
  const [clientName, setClientName] = useState(wizardClientName || 'Client');
  const lastSeededClientRef = useRef(wizardClientName);
  useEffect(() => {
    if (wizardClientName && wizardClientName !== lastSeededClientRef.current) {
      setClientName(wizardClientName);
      lastSeededClientRef.current = wizardClientName;
    }
  }, [wizardClientName]);

  // Auto-generated, user-editable services description
  const initialServices = useMemo(
    () => defaultServicesText(wizardData.services),
    [wizardData.services],
  );
  const [servicesText, setServicesText] = useState(initialServices);
  useEffect(() => {
    setServicesText(initialServices);
  }, [initialServices]);

  // Other editable variables
  const [firmName,     setFirmName]     = useState(DEFAULT_PROPOSAL_VARIABLES.firm_name);
  const [validityDays, setValidityDays] = useState(DEFAULT_PROPOSAL_VARIABLES.validity_days);

  // Pre-load logo once
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    loadLogoData(LOGO_PATH).then(setLogo).catch(() => setLogo(null));
  }, []);

  const variables = useMemo(() => ({
    client_name:      clientName || 'Client',
    engagement_name:  wizardData.componentName || wizardData.selectedTemplate?.clientName || 'Engagement',
    bid_amount:       formatCurrency(bidValue),
    bid_amount_label: isRetainer ? 'monthly retainer' : 'total project fee',
    services:         servicesText,
    date:             todayLabel(),
    firm_name:        firmName,
    validity_days:    String(validityDays),
  }), [clientName, wizardData.componentName, wizardData.selectedTemplate, bidValue, isRetainer, servicesText, firmName, validityDays]);

  const renderedProposal = useMemo(
    () => fillPlaceholders(bodyText, variables),
    [bodyText, variables],
  );

  const handleResetBody = useCallback(() => {
    setBodyText(selectedTemplate.body);
  }, [selectedTemplate.body]);

  // ── PDF building (shared by preview + download) ─────────────────────────
  const buildPdfDoc = useCallback(() => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });

    const marginX      = 54;       // 0.75"
    const marginTop    = 56;
    const marginBottom = 54;
    const pageWidth    = doc.internal.pageSize.getWidth();
    const pageHeight   = doc.internal.pageSize.getHeight();
    const usableWidth  = pageWidth - marginX * 2;
    const lineHeight   = 14;

    let cursorY = marginTop;

    // Header: logo left-aligned only, no competing element on the right
    if (logo) {
      const logoHeight = 28;
      const logoWidth  = (logo.width / logo.height) * logoHeight;
      doc.addImage(logo.dataUrl, 'PNG', marginX, cursorY, logoWidth, logoHeight);
    }
    cursorY += 44;

    // Brand accent bar
    doc.setFillColor(BRAND.orange);
    doc.rect(marginX, cursorY, usableWidth, 3, 'F');
    cursorY += 20;

    // Body
    doc.setFontSize(11);
    doc.setTextColor('#222222');

    renderedProposal.split('\n').forEach((line) => {
      const isHeading = line.length > 0 && line === line.toUpperCase() && /[A-Z]/.test(line);

      if (isHeading) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(BRAND.darkblue);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor('#222222');
      }

      if (line.trim() === '') {
        cursorY += lineHeight * 0.6;
        return;
      }

      const wrapped = doc.splitTextToSize(line, usableWidth);
      wrapped.forEach((wrappedLine) => {
        if (cursorY > pageHeight - marginBottom) {
          doc.addPage();
          cursorY = marginTop;
        }
        doc.text(wrappedLine, marginX, cursorY);
        cursorY += isHeading ? lineHeight + 2 : lineHeight;
      });
    });

    // Footer band on every page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(BRAND.greyLine);
      doc.setLineWidth(0.5);
      doc.line(marginX, pageHeight - 36, pageWidth - marginX, pageHeight - 36);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(BRAND.grey);
      doc.text(variables.firm_name, marginX, pageHeight - 22);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 22, { align: 'right' });
    }

    return doc;
  }, [renderedProposal, variables.firm_name, logo]);

  const fileName = useMemo(() => {
    const safeName = (variables.client_name || 'proposal')
      .replace(/[^a-z0-9]+/gi, '_')
      .toLowerCase();
    return `proposal_${safeName}.pdf`;
  }, [variables.client_name]);

  const handleDownloadPdf = useCallback(() => {
    buildPdfDoc().save(fileName);
  }, [buildPdfDoc, fileName]);

  // ── Edit body modal state ───────────────────────────────────────────────
  const [showEditor, setShowEditor] = useState(false);

  // ── Preview modal state ─────────────────────────────────────────────────
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl,      setPdfUrl]      = useState(null);

  const openPreview = useCallback(() => {
    const doc  = buildPdfDoc();
    const blob = doc.output('blob');
    const url  = URL.createObjectURL(blob);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setShowPreview(true);
  }, [buildPdfDoc]);

  const closePreview = useCallback(() => {
    setShowPreview(false);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div data-tour="step5-header">
        <h2 className="text-lg font-bold text-darkblue-500">Build Proposal</h2>
        <p className="text-xs text-grey-600 mt-0.5">
          Pick a template, edit the language, preview the PDF, and download it for the client.
        </p>
      </div>

      {/* ── Bid summary banner ── */}
      <div data-tour="step5-summary" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-3 bg-darkblue-50 rounded-lg border border-darkblue-100">
          <p className="text-xs text-darkblue-500 mb-0.5">Client</p>
          <p className="text-sm font-semibold text-darkblue-700 truncate">
            {variables.client_name}
          </p>
        </div>
        <div className="p-3 bg-darkblue-50 rounded-lg border border-darkblue-100">
          <p className="text-xs text-darkblue-500 mb-0.5">Engagement</p>
          <p className="text-sm font-semibold text-darkblue-700 truncate">
            {variables.engagement_name}
          </p>
        </div>
        <div className="p-3 bg-brand-50 rounded-lg border border-brand-200">
          <p className="text-xs text-brand-700 mb-0.5">{variables.bid_amount_label}</p>
          <p className="text-sm font-bold text-brand-600">{variables.bid_amount}</p>
        </div>
      </div>

      {/* ── Template dropdown ── */}
      <div data-tour="step5-template">
        <label className="text-xs font-semibold text-darkblue-500 uppercase tracking-wider mb-1.5 block">
          Template
        </label>
        <select
          value={selectedTemplateId}
          onChange={(e) => setSelectedTemplateId(e.target.value)}
          className="w-full max-w-md px-3 py-2 text-sm border border-grey-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
        >
          {PROPOSAL_TEMPLATES.map((tmpl) => (
            <option key={tmpl.id} value={tmpl.id}>
              {tmpl.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-grey-500 mt-1">{selectedTemplate.description}</p>
      </div>

      {/* ── Variables row ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-grey-700 mb-1 block">Client Name</label>
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-grey-700 mb-1 block">Firm Name</label>
          <input
            type="text"
            value={firmName}
            onChange={(e) => setFirmName(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-grey-700 mb-1 block">Validity (days)</label>
          <input
            type="number"
            min="1"
            value={validityDays}
            onChange={(e) => setValidityDays(Number(e.target.value) || 0)}
            className="w-full px-3 py-1.5 text-sm border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
          />
        </div>
      </div>

      {/* ── Services description (editable) ── */}
      <div>
        <label className="text-xs font-medium text-grey-700 mb-1 flex items-center justify-between">
          <span>Description of Services <span className="text-grey-500">(replaces {`{{services}}`})</span></span>
        </label>
        <textarea
          rows={4}
          value={servicesText}
          onChange={(e) => setServicesText(e.target.value)}
          className="w-full px-3 py-2 text-sm font-mono border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400 resize-y"
        />
      </div>

      {/* ── Template body — opens in a modal ── */}
      <div className="flex items-center justify-between gap-3 p-3 border border-grey-300 rounded-lg bg-grey-50">
        <div className="min-w-0">
          <p className="text-xs font-medium text-darkblue-500 flex items-center gap-1.5">
            <PencilSquareIcon className="w-3.5 h-3.5" />
            Template Body
            {bodyText !== selectedTemplate.body && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-brand-100 text-brand-700 rounded">
                edited
              </span>
            )}
          </p>
          <p className="text-xs text-grey-600 mt-0.5 truncate">
            {bodyText.replace(/\n+/g, ' ').slice(0, 90)}{bodyText.length > 90 ? '…' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowEditor(true)}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-darkblue-500 text-darkblue-500 hover:bg-darkblue-50 text-xs font-medium transition-colors"
        >
          <PencilSquareIcon className="w-3.5 h-3.5" />
          Edit Body
        </button>
      </div>

      {/* ── Action row ── */}
      <div data-tour="step5-actions" className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={openPreview}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-darkblue-500 text-darkblue-500 hover:bg-darkblue-50 text-sm font-medium transition-colors"
        >
          <EyeIcon className="w-4 h-4" />
          Preview PDF
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 text-white hover:bg-brand-600 text-sm font-medium transition-colors"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* ── PDF Preview Modal ── */}
      {showPreview && pdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closePreview}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col">

            {/* Header */}
            <div className="px-5 py-3 border-b border-grey-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-darkblue-500">Proposal Preview</h3>
                <p className="text-xs text-grey-500 mt-0.5">{fileName}</p>
              </div>
              <button
                onClick={closePreview}
                className="p-1.5 text-grey-500 hover:text-darkblue-500 hover:bg-grey-100 rounded-lg transition-colors"
                title="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* PDF iframe */}
            <div className="flex-1 bg-grey-100 overflow-hidden">
              <iframe
                key={pdfUrl}
                src={pdfUrl}
                title="Proposal PDF preview"
                className="w-full h-full border-0"
              />
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-grey-200 flex items-center justify-between">
              <button
                onClick={openPreview}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-grey-700 hover:text-darkblue-500 border border-grey-300 rounded-lg hover:bg-grey-100 transition-colors"
                title="Re-render PDF with latest edits"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                Refresh
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={closePreview}
                  className="px-3 py-1.5 text-xs font-medium text-grey-700 border border-grey-300 rounded-lg hover:bg-grey-100 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleDownloadPdf}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-brand-500 text-white hover:bg-brand-600 text-xs font-medium transition-colors"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Template Body Editor Modal ── */}
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowEditor(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="px-5 py-3 border-b border-grey-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-darkblue-500 flex items-center gap-1.5">
                  <PencilSquareIcon className="w-4 h-4" />
                  Edit Template Body
                </h3>
                <p className="text-xs text-grey-500 mt-0.5">
                  Changes apply live to the preview &amp; PDF.
                </p>
              </div>
              <button
                onClick={() => setShowEditor(false)}
                className="p-1.5 text-grey-500 hover:text-darkblue-500 hover:bg-grey-100 rounded-lg transition-colors"
                title="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Editor */}
            <div className="flex-1 overflow-auto p-5">
              <textarea
                autoFocus
                rows={20}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                className="w-full px-3 py-2 text-xs font-mono border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400 resize-y"
              />
              <p className="text-[11px] text-grey-500 mt-2">
                Placeholders: <code>{`{{client_name}}`}</code>, <code>{`{{engagement_name}}`}</code>,{' '}
                <code>{`{{bid_amount}}`}</code>, <code>{`{{services}}`}</code>, <code>{`{{date}}`}</code>,{' '}
                <code>{`{{firm_name}}`}</code>, <code>{`{{validity_days}}`}</code>
              </p>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-grey-200 flex items-center justify-between">
              <button
                onClick={handleResetBody}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-grey-700 hover:text-darkblue-500 border border-grey-300 rounded-lg hover:bg-grey-100 transition-colors"
                title="Reset to template default"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                Reset to default
              </button>
              <button
                onClick={() => setShowEditor(false)}
                className="px-4 py-1.5 text-xs font-medium bg-darkblue-500 text-white rounded-lg hover:bg-darkblue-600 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step5GenerateProposal;
